import { CalendarCheck, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DELAI_AMO_MOIS, type LigneRenouvellement } from '@sirajoptic/shared';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader } from '@/components/ui/card';
import { EtatVide } from '@/components/ui/feedback';
import { date, entier, lienTelephone, telephone } from '@/lib/format';

/**
 * Liste de rappel AMO — les clients qui franchissent leurs deux ans ce mois-ci.
 *
 * C'est **le mois** qui est montré, pas le retard accumulé. Sur un fichier qui
 * remonte à 2017, « tous les clients renouvelables » ce sont des milliers de
 * fiches, et personne n'appelle une liste de mille noms. Ceux dont le délai
 * échoit ce mois-ci se comptent par dizaines : là, on décroche.
 *
 * Le numéro est un lien `tel:` — sur le téléphone du magasin, la liste devient
 * la liste d'appels elle-même.
 */
export function RenouvellementsCard({
  lignes,
  total,
}: {
  lignes: LigneRenouvellement[];
  total: number;
}) {
  // Vrai tant que l'archive importée n'a pas de date de prescription.
  const approximatif = lignes.some((l) => l.depart_estime);

  return (
    <Card>
      <CardHeader
        titre="Renouvellements AMO"
        description={`Clients atteignant ${DELAI_AMO_MOIS} mois ce mois-ci.`}
        action={
          total > 0 ? (
            <Badge ton="moutarde">{entier(total)}</Badge>
          ) : null
        }
      />

      {lignes.length === 0 ? (
        <EtatVide
          titre="Personne ce mois-ci"
          description={`Aucun client n'atteint ses ${DELAI_AMO_MOIS} mois en ce moment.`}
          icone={<CalendarCheck aria-hidden className="h-6 w-6" />}
        />
      ) : (
        <>
          <ul className="divide-y divide-line">
            {lignes.map((l) => (
              <li key={l.client_id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/clients/${l.client_id}`}
                    className="block truncate text-sm font-medium text-ink hover:text-mustard-700 hover:underline"
                  >
                    {l.client_nom}
                  </Link>
                  <p className="text-xs text-ink-muted">
                    Dernier équipement{' '}
                    <span
                      className={
                        l.depart_estime ? 'border-b border-dotted border-ink-muted' : undefined
                      }
                      title={
                        l.depart_estime
                          ? "Date de la commande : l'ordonnance n'a pas été datée."
                          : "Date de l'ordonnance."
                      }
                    >
                      {date(l.depart)}
                    </span>
                    {' · renouvelable le '}
                    {date(l.echeance)}
                  </p>
                </div>

                <Appel tel={l.tel} />
              </li>
            ))}
          </ul>

          {/* Dit une fois, en bas, plutôt que sur chaque ligne : aujourd'hui
              aucune commande de l'archive ne porte de date d'ordonnance, le
              marquer partout ne signalerait plus rien. */}
          {approximatif ? (
            <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">
              Les dates soulignées sont celles de la commande — l'ordonnance n'avait pas été
              datée. Le délai peut être avancé de quelques jours.
            </p>
          ) : null}

          {total > lignes.length ? (
            <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">
              {entier(total - lignes.length)} autre{total - lignes.length > 1 ? 's' : ''} ce
              mois-ci.
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

/**
 * Le numéro, en bouton d'appel quand il en est un.
 *
 * Un millier de fiches portent autre chose qu'un numéro dans ce champ — deux
 * numéros, un nom, une note. Le texte reste affiché, sans lien : l'opticien
 * reconnaît sa donnée, et rien ne promet un appel qui échouerait.
 */
function Appel({ tel }: { tel: string | null }) {
  if (!tel?.trim()) {
    return <span className="shrink-0 text-xs text-ink-muted">Sans téléphone</span>;
  }

  const lien = lienTelephone(tel);
  if (!lien) {
    return (
      <span
        className="max-w-[9rem] shrink-0 truncate text-right text-xs text-ink-muted"
        title={tel}
      >
        {tel}
      </span>
    );
  }

  return (
    <a
      href={lien}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line px-2 py-1
        text-xs font-medium text-ink transition-colors hover:border-mustard-500 hover:bg-mustard-50"
    >
      <Phone aria-hidden className="h-3 w-3" />
      <span className="tabular">{telephone(tel)}</span>
    </a>
  );
}
